/**
 * Field-validation error summary + focus (AJAX / full postback).
 *
 * Library ssks/form_error_summary — attached on every form via
 * ssks_form_alter(), not bundled in ssks.js. Only runs when field errors
 * (or a summary) exist.
 * Does not relocate status/mail toasts (ssks/form_webform_feedback).
 *
 * AJAX: focus the h2 (title + links in .visually-hidden); remount on submit.
 * noactivate: focus the first error link (aria-label = title + count + error).
 */
import {
  FIELD_ERROR_SELECTOR,
  FEEDBACK_CLASS,
  SUMMARY_CLASS,
  claimFocus,
  createScheduler,
  ensureFocusable,
  focusPreventScroll,
  isSafeDomId,
  sanitizeDomText,
  safeDomToken,
  safeHashHref,
  scrollBelowSticky,
  syncToastVisibility,
  lang,
  translate,
} from "./form-a11y-shared";

(function (Drupal, $) {
  const INVALID_CONTROL_SELECTOR =
    'form [aria-invalid="true"], form input.error, form textarea.error, form select.error';

  const supportsHasSelector = (() => {
    try {
      return (
        typeof CSS !== "undefined" && CSS.supports("selector(:has(*))")
      );
    } catch (e) {
      return false;
    }
  })();

  /**
   * État par formulaire — deux formulaires en erreur sur la même page ne se
   * marchent plus dessus.
   *
   * @type {WeakMap<HTMLFormElement, {lastSignature: string, remount: boolean}>}
   */
  const formStates = new WeakMap();

  function stateFor(form) {
    let state = formStates.get(form);
    if (!state) {
      state = { lastSignature: "", remount: false };
      formStates.set(form, state);
    }
    return state;
  }

  // Flags liés au transport AJAX (globaux : ajaxSend/ajaxComplete ne disent
  // pas de façon fiable quel formulaire a déclenché la requête).
  /** Form AJAX in flight — attach must not take the noactivate (first-link) path. */
  let formAjaxPending = false;
  /** Fresh heading after each form AJAX so the title is announced again. */
  let remountAllOnNextBuild = false;
  /** Force la (ré)annonce au prochain handle, même à signature identique. */
  let forceAnnounce = false;
  /** Unique heading ids per announce (reusing an id silences a second AJAX). */
  let announceGen = 0;
  /**
   * Dernier ajaxComplete d'un AJAX de formulaire. Horodatage plutôt que
   * flag consommable : les commandes Drupal sont asynchrones, ajaxComplete
   * peut précéder l'insert du nouveau DOM — un flag consommé par un handle
   * trop tôt faisait prendre la branche postback au handle déclenché par
   * l'attach post-insert (focus lien au 1er submit, titre au 2e).
   */
  let lastFormAjaxAt = 0;

  function isAjaxContext() {
    return performance.now() - lastFormAjaxAt < 2000;
  }

  /**
   * Drupal form AJAX (webform ajax_form=1), not BigPipe / unrelated XHR.
   *
   * @param {object} [settings]
   * @return {boolean}
   */
  function isDrupalFormAjax(settings) {
    if (!settings || typeof settings !== "object") {
      return false;
    }
    const url = String(settings.url || "");
    if (/ajax_form=1|_wrapper_format=drupal_ajax/i.test(url)) {
      return true;
    }
    return !!(
      settings.extraData && settings.extraData._triggering_element_name
    );
  }

  /** Code langue normalisé ("fr-FR" → "fr"). */
  function langCode() {
    return lang().split("-")[0].toLowerCase();
  }

  function titleText() {
    switch (langCode()) {
      case "fr":
        return translate("Le formulaire n'a pas pu être envoyé");
      case "de":
        return translate("Das Formular konnte nicht gesendet werden");
      case "es":
        return translate("El formulario no se pudo enviar");
      case "it":
        return translate("Impossibile inviare il modulo");
      default:
        return translate("The form could not be submitted");
    }
  }

  function countText(count) {
    const n = String(count);
    switch (langCode()) {
      case "fr":
        return count === 1
          ? translate("1 erreur :")
          : translate("@count erreurs :", { "@count": n });
      case "de":
        return count === 1
          ? translate("1 Fehler:")
          : translate("@count Fehler:", { "@count": n });
      case "es":
        return count === 1
          ? translate("1 error:")
          : translate("@count errores:", { "@count": n });
      case "it":
        return count === 1
          ? translate("1 errore:")
          : translate("@count errori:", { "@count": n });
      default:
        return count === 1
          ? translate("1 error:")
          : translate("@count errors:", { "@count": n });
    }
  }

  function getErrorMessage(control, form) {
    const id = control.id;
    if (isSafeDomId(id)) {
      const byId = form.querySelector(`#${CSS.escape(id)}--error`);
      if (byId) {
        return sanitizeDomText(byId.textContent);
      }
    }
    const item = control.closest(
      ".form-item, .js-form-item, fieldset, details"
    );
    const msg = item && item.querySelector(".form-item--error-message");
    if (msg) {
      return sanitizeDomText(msg.textContent);
    }
    const wrapLabel =
      item && item.querySelector("label, .fieldset-legend, summary");
    return sanitizeDomText(wrapLabel ? wrapLabel.textContent : id || "");
  }

  function collectInvalidControls(form) {
    const seen = new Set();
    return [...form.querySelectorAll(INVALID_CONTROL_SELECTOR)].filter(
      (el) => {
        if (!(el instanceof HTMLElement) || !isSafeDomId(el.id)) {
          return false;
        }
        if (seen.has(el.id)) {
          return false;
        }
        const hasMsg =
          form.querySelector(`#${CSS.escape(el.id)}--error`) ||
          (
            el.closest(".form-item, .js-form-item, fieldset, details") || el
          ).querySelector?.(".form-item--error-message");
        if (!hasMsg) {
          return false;
        }
        seen.add(el.id);
        return true;
      }
    );
  }

  function controlsSignature(controls, form) {
    return controls
      .map((c) => `${c.id}:${getErrorMessage(c, form)}`)
      .join("|");
  }

  function formDomId(form) {
    return safeDomToken(form.getAttribute("id"), "form");
  }

  function decorateFirstLinkWithTitle(summary) {
    const title = summary.querySelector(`:scope > .${SUMMARY_CLASS}__title`);
    const count = summary.querySelector(`:scope > .${SUMMARY_CLASS}__count`);
    const titleLabel =
      title instanceof HTMLElement
        ? sanitizeDomText(title.textContent) || titleText()
        : titleText();
    const countLabel =
      count instanceof HTMLElement ? sanitizeDomText(count.textContent) : "";
    summary.querySelectorAll(`.${SUMMARY_CLASS}__link`).forEach((link, i) => {
      if (i === 0) {
        const msg = sanitizeDomText(link.textContent);
        link.setAttribute(
          "aria-label",
          [titleLabel, countLabel, msg].filter(Boolean).join(". ")
        );
      } else {
        link.removeAttribute("aria-label");
      }
    });
  }

  function normalizeSummaryA11y(summary, ajaxMode) {
    summary.removeAttribute("role");
    summary.removeAttribute("aria-live");
    summary.removeAttribute("aria-describedby");
    summary.removeAttribute("aria-labelledby");

    const title = summary.querySelector(`:scope > .${SUMMARY_CLASS}__title`);
    const count = summary.querySelector(`:scope > .${SUMMARY_CLASS}__count`);
    const links = [...summary.querySelectorAll(`.${SUMMARY_CLASS}__link`)];

    if (!(title instanceof HTMLElement)) {
      return;
    }

    const visibleTitle =
      sanitizeDomText(
        [...title.childNodes]
          .filter(
            (n) =>
              !(
                n instanceof HTMLElement &&
                n.classList.contains("visually-hidden")
              )
          )
          .map((n) => n.textContent)
          .join("")
      ) || titleText();

    title.textContent = visibleTitle;
    title.removeAttribute("aria-describedby");
    title.removeAttribute("aria-label");
    links.forEach((link) => {
      link.removeAttribute("tabindex");
      link.removeAttribute("aria-label");
    });

    if (ajaxMode) {
      summary.removeAttribute("tabindex");
      summary.removeAttribute("aria-label");
      const srParts = [
        count instanceof HTMLElement ? sanitizeDomText(count.textContent) : "",
        ...links.map((link) => sanitizeDomText(link.textContent)),
      ].filter(Boolean);
      if (srParts.length) {
        const sr = document.createElement("span");
        sr.className = "visually-hidden";
        sr.textContent = `. ${srParts.join(". ")}`;
        title.appendChild(sr);
      }
      if (count instanceof HTMLElement) {
        count.setAttribute("aria-hidden", "true");
      }
      title.setAttribute("tabindex", "-1");
      return;
    }

    title.removeAttribute("tabindex");
    summary.removeAttribute("tabindex");
    summary.removeAttribute("aria-label");
    if (count instanceof HTMLElement) {
      count.removeAttribute("aria-hidden");
    }
    decorateFirstLinkWithTitle(summary);
  }

  function insertSummary(form, summary) {
    const legend = form.querySelector(":scope > .form-required-legend");
    const anchor = form.querySelector(
      ":scope > .webform-progress, :scope > .webform-confirmation"
    );
    if (legend) {
      legend.insertAdjacentElement("beforebegin", summary);
    } else if (anchor) {
      anchor.insertAdjacentElement("afterend", summary);
    } else {
      form.insertBefore(summary, form.firstChild);
    }
  }

  function buildSummary(form, controls, ajaxMode) {
    const state = stateFor(form);
    const signature = controlsSignature(controls, form);
    let existing = form.querySelector(`:scope > .${SUMMARY_CLASS}`);

    if ((state.remount || remountAllOnNextBuild) && existing) {
      existing.remove();
      existing = null;
    }
    state.remount = false;

    if (
      existing instanceof HTMLElement &&
      existing.getAttribute("data-ssks-signature") === signature
    ) {
      normalizeSummaryA11y(existing, ajaxMode);
      return existing;
    }
    if (existing) {
      existing.remove();
    }

    const id = formDomId(form);
    announceGen += 1;
    const gen = String(announceGen);
    const summary = document.createElement("div");
    summary.className = SUMMARY_CLASS;
    summary.setAttribute("data-ssks-form-error-summary", "true");
    summary.setAttribute("data-ssks-signature", signature);

    const title = document.createElement("h2");
    title.className = `${SUMMARY_CLASS}__title`;
    title.id = `ssks-form-error-title-${id}-${gen}`;
    title.textContent = titleText();
    summary.appendChild(title);

    const countEl = document.createElement("p");
    countEl.className = `${SUMMARY_CLASS}__count`;
    countEl.id = `ssks-form-error-count-${id}-${gen}`;
    countEl.textContent = countText(controls.length);
    summary.appendChild(countEl);

    const list = document.createElement("ul");
    list.className = `${SUMMARY_CLASS}__list`;
    list.id = `ssks-form-error-list-${id}-${gen}`;

    controls.forEach((control) => {
      const li = document.createElement("li");
      const link = document.createElement("a");
      const href = safeHashHref(control.id);
      if (!href) {
        return;
      }
      link.href = href;
      link.className = `${SUMMARY_CLASS}__link`;
      link.textContent = getErrorMessage(control, form) || `#${control.id}`;
      link.addEventListener("click", (event) => {
        if (
          document.getElementById(control.id) === control &&
          form.contains(control) &&
          typeof control.focus === "function"
        ) {
          event.preventDefault();
          focusPreventScroll(control);
          scrollBelowSticky(control, "smooth");
        }
      });
      li.appendChild(link);
      list.appendChild(li);
    });

    summary.appendChild(list);
    normalizeSummaryA11y(summary, ajaxMode);
    insertSummary(form, summary);
    return summary;
  }

  function focusSummary(summary, ajaxMode) {
    normalizeSummaryA11y(summary, ajaxMode);
    const title = summary.querySelector(`:scope > .${SUMMARY_CLASS}__title`);
    const firstLink = summary.querySelector(`.${SUMMARY_CLASS}__link`);
    if (!(title instanceof HTMLElement)) {
      return;
    }

    syncToastVisibility("error", true);
    scrollBelowSticky(summary, "auto");

    if (ajaxMode) {
      ensureFocusable(title);
      if (document.activeElement === title) {
        title.blur();
      }
      // Drupal.ajax restores focus to the submit after ajaxComplete.
      // Wait, then claim the heading so the announcement is not interrupted.
      window.setTimeout(() => {
        if (title.isConnected) {
          claimFocus(title);
        }
      }, 150);
      return;
    }
    if (!(firstLink instanceof HTMLElement)) {
      return;
    }
    if (document.activeElement === firstLink) {
      firstLink.blur();
    }
    claimFocus(firstLink);
  }

  /**
   * Forms containing field errors. `:has()` quand il est supporté, sinon
   * repli par scan des formulaires — un querySelectorAll avec un sélecteur
   * inconnu jette une SyntaxError et tuerait tout le handler.
   *
   * @return {Element[]}
   */
  function formsWithFieldErrors() {
    if (supportsHasSelector) {
      return [
        ...document.querySelectorAll(
          'form:has([aria-invalid="true"]), form:has(.form-item--error-message)'
        ),
      ];
    }
    return [...document.querySelectorAll("form")].filter(
      (form) =>
        form.querySelector('[aria-invalid="true"]') ||
        form.querySelector(".form-item--error-message")
    );
  }

  function handleFieldErrors(ajaxMode) {
    const forms = formsWithFieldErrors();

    let hasSummary = false;
    /** Premier summary (ordre DOM) dont la signature a changé. */
    let focusTarget = null;

    forms.forEach((form) => {
      if (!(form instanceof HTMLFormElement)) {
        return;
      }
      const controls = collectInvalidControls(form);
      if (!controls.length) {
        form.querySelector(`:scope > .${SUMMARY_CLASS}`)?.remove();
        stateFor(form).lastSignature = "";
        return;
      }
      const summary = buildSummary(form, controls, !!ajaxMode);
      hasSummary = true;

      const state = stateFor(form);
      const signature = summary.getAttribute("data-ssks-signature") || "";
      const changed = forceAnnounce || signature !== state.lastSignature;
      state.lastSignature = signature;
      if (changed && !focusTarget) {
        focusTarget = summary;
      }
    });

    remountAllOnNextBuild = false;
    forceAnnounce = false;

    syncToastVisibility("error", hasSummary);

    if (!hasSummary) {
      return;
    }

    // Drop in-form status feedback if field errors take over.
    document
      .querySelectorAll(`form > .${FEEDBACK_CLASS}`)
      .forEach((el) => el.remove());

    if (focusTarget) {
      focusSummary(focusTarget, !!ajaxMode);
    }
  }

  const scheduleHandle = createScheduler(() => {
    const ajaxMode = isAjaxContext();
    if (document.querySelector(FIELD_ERROR_SELECTOR)) {
      syncToastVisibility("error", true);
    }
    handleFieldErrors(ajaxMode);
  });

  /**
   * Validation du navigateur coupée sur les formulaires POST hors webform
   * (newsletter…) : Drupal revalide côté serveur et le résumé ci-dessus
   * remplace les bulles. Les webforms gardent leur réglage du back-office
   * (validation côté client). Posé en JS car, sur un formulaire rendu dans
   * un bloc, les #attributes PHP sont reportés sur le wrapper du bloc. Les
   * GET (recherche, filtres) ne sont pas touchés.
   */
  Drupal.behaviors.ssksFormNoValidate = {
    attach(context) {
      once(
        "ssks-form-novalidate",
        "form[method='post' i]:not(.webform-submission-form):not([data-drupal-selector*='webform'])",
        context
      ).forEach((form) => {
        form.noValidate = true;
      });
    },
  };

  Drupal.behaviors.ssksFormErrorSummary = {
    attach() {
      if (formAjaxPending) {
        return;
      }
      if (
        !document.querySelector(FIELD_ERROR_SELECTOR) &&
        !document.querySelector(`.${SUMMARY_CLASS}`)
      ) {
        return;
      }
      scheduleHandle();
    },
  };

  document.addEventListener(
    "submit",
    (event) => {
      if (!(event.target instanceof HTMLFormElement)) {
        return;
      }
      const state = stateFor(event.target);
      state.lastSignature = "";
      state.remount = true;
      if (
        event.target.querySelector(".ajax-processed, [data-drupal-ajax]")
      ) {
        formAjaxPending = true;
      }
    },
    true
  );

  $(document).on(
    "ajaxSend.ssksFormErrorSummary",
    (_event, _xhr, settings) => {
      if (!isDrupalFormAjax(settings)) {
        return;
      }
      formAjaxPending = true;
      remountAllOnNextBuild = true;
    }
  );

  $(document).on(
    "ajaxComplete.ssksFormErrorSummary",
    (_event, _xhr, settings) => {
      if (!isDrupalFormAjax(settings) && !formAjaxPending) {
        return;
      }
      formAjaxPending = false;
      lastFormAjaxAt = performance.now();
      remountAllOnNextBuild = true;
      forceAnnounce = true;
      // Pas de garde "needsWork" ici : à cet instant le DOM peut être
      // encore l'ancien (insert pas exécuté). L'attach post-insert
      // replanifie de toute façon ; ce schedule couvre le cas où aucun
      // attach ne suit (réponse sans insert).
      scheduleHandle();
    }
  );
})(Drupal, jQuery);
